import { niceAxis, type NiceAxis } from '@ardoq/math';
import type { NumericRange } from '../types';
import { maxOf, minOf } from '../data/ranges';

const TURN = Math.PI * 2;

const padDegenerate = ([min, max]: NumericRange): NumericRange =>
  min === max ? [min - 0.5, max + 0.5] : [min, max];

/** A radius axis from zero, or from the lowest value when some are negative. */
export const toRadialAxis = (values: number[]): NiceAxis => {
  if (values.length === 0) return { range: [0, 1], tickCount: 4 };

  const extent = padDegenerate([
    Math.min(0, minOf(values)),
    Math.max(0, maxOf(values)),
  ]);
  return niceAxis(extent);
};

/**
 * An angle axis over the values' nice range plus one more step, so the largest value sits one tick
 * before twelve o'clock instead of on top of the smallest.
 */
export const toAngularAxis = (values: number[]): NiceAxis => {
  if (values.length === 0) return { range: [0, 1], tickCount: 4 };

  const {
    range: [min, max],
    tickCount,
  } = niceAxis(padDegenerate([minOf(values), maxOf(values)]));
  const step = (max - min) / tickCount;
  return { range: [min, max + step], tickCount: tickCount + 1 };
};

export const toAngle = (
  { range: [min, max] }: NiceAxis,
  value: number
): number => ((value - min) / (max - min)) * TURN;

export const toSpokeAngle = (index: number, spokeCount: number): number =>
  (index / spokeCount) * TURN;

/** The angular ticks, leaving out the last, which lands on the first. */
export const toAngularTicks = (
  axis: NiceAxis
): { value: number; angle: number }[] => {
  const {
    range: [min, max],
    tickCount,
  } = axis;
  return Array.from({ length: tickCount }, (_, index) => {
    const value = min + (index / tickCount) * (max - min);
    return { value, angle: toAngle(axis, value) };
  });
};

export const toRadialTicks = ({
  range: [min, max],
  tickCount,
}: NiceAxis): number[] =>
  Array.from(
    { length: tickCount + 1 },
    (_, index) => min + (index / tickCount) * (max - min)
  );
